if (!customElements.get('wlsn-newsletter-form')) {
  class WLSNNewsletterForm extends HTMLElement {
    constructor() {
      super();

      this.classes = {
        active: 'active',
        feedbackMessage: 'js-feedback-message',
        emailInput: 'input[name="contact[email]"]',
        sectionId: `${this.getAttribute('data-newsletter-form')}`,
        loading: 'loading',
      };

      this.apiUrl = 'https://manage.kmail-lists.com/ajax/subscriptions/subscribe';
      this.listGroup = window.theme?.klaviyoId;

      this.feedbackMessages = {
        successMessage: 'Thank you for subscribing!',
        validationEmailMessage: 'Please enter a valid email address.',
        alreadySubscribedMessage: 'You have already subscribed.',
        unexpectedErrorMessage: 'An error occurred. Please try again later.',
      };
    }

    /**
     * Get DOM elements
     * @returns {object} - Object containing DOM element references
     */
    get elements() {
      return {
        submitButton: this.querySelector('button.js-newsletter-submit[type="submit"]'),
        feedbackMessage: this.querySelector(`.${this.classes.feedbackMessage}`),
        form: this.querySelector('form'),
        emailInput: this.querySelector(`${this.classes.emailInput}`),
      };
    }

    /**
     * Connected callback lifecycle method
     * @returns {void}
     */
    connectedCallback() {
      this._loadSettings();
      this._bindEvents();
    }

    /**
     * Bind event listeners
     * @returns {void}
     */
    _bindEvents() {
      if (this.listGroup) {
        this.elements.submitButton.addEventListener('click', this._handleSubmit.bind(this));
      } else {
        this.elements.submitButton.setAttribute('disabled', 'true');
      }
    }

    /**
     * Load feedback messages from settings element
     * @returns {void}
     */
    _loadSettings() {
      const settingsElement = document.getElementById(`NewsletterFeedbackMessages-${this.classes.sectionId}`);

      if (settingsElement) {
        const incomingMessages = JSON.parse(settingsElement.textContent);

        Object.keys(incomingMessages).forEach((key) => {
          if (incomingMessages[key]) {
            this.feedbackMessages[key] = incomingMessages[key];
          }
        });
      }
    }

    /**
     * Handle form submit event
     * @param {Event} e - The submit event
     * @returns {void}
     */
    _handleSubmit(e) {
      e.preventDefault();
      const isEmail = this._isEmail(this.elements.emailInput.value);
      this.elements.submitButton.classList.add(this.classes.loading);

      if (isEmail) {
        this._subscribeEmail(this.elements.emailInput.value, this.elements.feedbackMessage);
      } else {
        this._displayMessage(this.elements.feedbackMessage, `${this.feedbackMessages.validationEmailMessage}`, 'error');
        this.elements.submitButton.classList.remove(this.classes.loading);
      }
    }

    /**
     * Subscribe email to Klaviyo list
     * @param {string} email - The email address to subscribe
     * @param {HTMLElement} messageSelector - The element to display feedback messages
     * @returns {Promise<void>}
     */
    async _subscribeEmail(email, messageSelector) {
      try {
        const response = await fetch(this.apiUrl, {
          headers: {
            accept: 'application/json, text/plain, */*',
            'content-type': 'application/x-www-form-urlencoded;charset=UTF-8',
          },
          body: new URLSearchParams({ g: this.listGroup, email }),
          method: 'POST',
        });

        const data = await response.json();

        if (data.data.is_subscribed) {
          this._displayMessage(messageSelector, `${this.feedbackMessages.alreadySubscribedMessage}`, 'error');
        } else if (data.success) {
          this._displayMessage(messageSelector, `${this.feedbackMessages.successMessage}`, 'success');
        }
      } catch (error) {
        this._displayMessage(messageSelector, `${this.feedbackMessages.unexpectedErrorMessage}`, 'error');
      } finally {
        this.elements.submitButton.classList.remove(this.classes.loading);
      }
    }

    /**
     * Display feedback message
     * @param {HTMLElement} selector - The element to display the message in
     * @param {string} message - The message to display
     * @param {string} type - The message type ('success' or 'error')
     * @returns {void}
     */
    _displayMessage(selector, message, type) {
      selector.innerHTML = message;
      selector.classList.toggle('success', type === 'success');
      selector.classList.toggle('error', type === 'error');
    }

    /**
     * Validate email address format
     * @param {string} email - The email address to validate
     * @returns {boolean} - Whether the email is valid
     */
    _isEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    }
  }

  window.customElements.define('wlsn-newsletter-form', WLSNNewsletterForm);
}
